<template>
    <!-- ALERTA -->
    <v-alert
        v-if="showAlert"
        :type="alertType"
        :class="[
            'mx-auto',
            'text-center',
            'bg-' + (alertType === 'success' ? 'green-lighten-4' : 'red-lighten-4')
        ]"
        style="position: fixed; top: 20px; left: 0; right: 0; z-index: 9999; max-width: 600px;"
        elevation="2"
        prominent
    >
        {{ alertMessage }}
    </v-alert>

    <!-- DIÀLEG PER PUJAR IMATGES -->
    <v-dialog v-model="dialog" max-width="400">
        <v-card>
            <v-card-title>
                <span class="text-h6">Actualitzar {{ dialogType === 'logo' ? 'Logo' : 'Imagen' }}</span>
            </v-card-title>
            <v-card-text>
                <v-file-input
                    v-model="selectedFile"
                    label="Selecciona un fichero"
                    accept="image/*"
                    prepend-icon="mdi-image"
                    dense
                />
            </v-card-text>
            <v-card-actions class="justify-end">
                <v-btn text @click="dialog = false">Cancelar</v-btn>
                <v-btn color="primary" @click="submitImageUpload">Subir</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <!-- LLISTAT -->
    <v-container fluid>
        <v-card class="pa-4" elevation="2">
            <v-row>
                <v-col class="headline mb-4">
                    <h2>Clientes</h2>
                </v-col>
                <v-col cols="12" sm="6" style="text-align: right">
                    <v-btn color="primary" :loading="isImporting" :disabled="isImporting" @click="importClients">
                        <template #loader>
                            <v-progress-circular indeterminate size="20" color="white" />
                        </template>
                        Importar de Odoo
                    </v-btn>
                    <v-btn style="margin-left: 10px" @click="$router.back()">Volver</v-btn>
                </v-col>
            </v-row>

            <v-row class="mb-4">
            <v-col cols="12" sm="6">
                <v-text-field
                v-model="search"
                label="Buscar por nombre o ID Odoo"
                prepend-inner-icon="mdi-magnify"
                clearable
                dense
                hide-details
                />
            </v-col>
            </v-row>
            <v-data-table
                :items="filteredClients"
                :headers="headers"
                class="elevation-1"
                no-data-text="No hi ha clients disponibles"
            >
                <!-- MENU PERSONALITZAT -->
                <template #item.has_custom_menu="{ item }">
                <v-switch
                    :model-value="Boolean(item.has_custom_menu)"
                    color="primary"
                    hide-details
                    inset
                    @update:modelValue="(val) => updateHasCustomMenu(item, val)"
                />
                </template>

                <!-- MAPA FOTOS -->
                <template #item.has_mapa_fotos="{ item }">
                    <v-switch
                    :model-value="Boolean(item.has_mapa_fotos)"
                    color="primary"
                    hide-details
                    inset
                    @update:modelValue="(val) => updateHasMapaFotos(item, val)"
                    />
                </template>

                <!-- LOGO MINIATURA -->
                <template #item.logo="{ item }">
                        <img :src="getImageUrl(item.id, item.logo)" alt="" height="40px" />
                </template>

                <!-- IMATGE MINIATURA -->
                <template #item.image="{ item }">
                    <img :src="getImageUrl(item.id, item.image)" alt="" height="40px" />
                </template>

                <!-- ACCIONS -->
                <template #item.actions="{ item }">
                        <v-btn class="ma-1" alt="Logo" icon @click="openDialog(item.id, 'logo')">
                            <v-icon>mdi-image-edit</v-icon>
                        </v-btn>
                        <v-btn class="ma-1" alt="Imagen" icon @click="openDialog(item.id, 'image')">
                            <v-icon>mdi-camera</v-icon>
                        </v-btn>
                </template>
            </v-data-table>
        </v-card>
    </v-container>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import axios from 'axios'
import { useRouter } from 'vue-router'

const router = useRouter()

const search = ref('')

// Buscador
const filteredClients = computed(() => {
  if (!search.value) return clients.value
  const term = search.value.toLowerCase()
  return clients.value.filter(client =>
    client.name?.toLowerCase().includes(term) ||
    String(client.odoo_id).includes(term)
  )
})

// Alertes
const alertMessage = ref('')
const alertType = ref('success')
const showAlert = ref(false)
const showAlertMessage = (message, type = 'success') => {
    alertMessage.value = message
    alertType.value = type
    showAlert.value = true
    setTimeout(() => (showAlert.value = false), 4000)
}

// Clients
const clients = ref([])
const isImporting = ref(false)
const fetchClients = async () => {
    try {
        const { data } = await axios.get('/api/admin/clients', {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
        // Normalitza a booleà per si ve com 0/1 o '0'/'1'
        clients.value = (Array.isArray(data) ? data : []).map(c => ({
            ...c,
            has_custom_menu: typeof c.has_custom_menu === 'boolean'
                ? c.has_custom_menu
                : Boolean(Number(c.has_custom_menu)),

            has_mapa_fotos: typeof c.has_mapa_fotos === 'boolean'
                ? c.has_mapa_fotos
                : Boolean(Number(c.has_mapa_fotos))
            }))
    } catch (e) {
        console.error('Error loading clients:', e)
        if (e.response?.status === 401) {
            localStorage.removeItem('token')
            await router.push('/')
        }
        showAlertMessage('Error cargando los clientes.', 'error')
    }
}

const importClients = async () => {
    isImporting.value = true
    showAlertMessage('Importando datos de Odoo...', 'success')
    try {
        await axios.post('/api/admin/clients/import', {}, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
        await fetchClients()
        showAlertMessage('Importación completada correctamente.', 'success')
    } catch (e) {
        console.error('Error importando clientes:', e)
        showAlertMessage('Error importando los clientes.', 'error')
    } finally {
        isImporting.value = false
    }
}

// Imatges
const getImageUrl = (clientId, filename) => {
    const ts = new Date().getTime()
    return `/storage/clients/${clientId}/${filename}?t=${ts}`
}

// Diàleg d'edició d’imatge
const dialog = ref(false)
const dialogClientId = ref(null)
const dialogType = ref(null) // 'logo' o 'image'
const selectedFile = ref(null)

const openDialog = (clientId, type) => {
    dialogClientId.value = clientId
    dialogType.value = type
    selectedFile.value = null
    dialog.value = true
}

const submitImageUpload = async () => {
    if (!selectedFile.value || !dialogClientId.value || !dialogType.value) return

    const formData = new FormData()
    formData.append('file', selectedFile.value)
    formData.append('type', dialogType.value)

    try {
        const res = await axios.post(`/api/admin/clients/${dialogClientId.value}/upload-media`, formData, {
            headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'multipart/form-data'
            }
        })

        showAlertMessage(res.data.message, 'success')
        await fetchClients()
        dialog.value = false
    } catch (e) {
        console.error('Error subiendo imagen:', e)
        showAlertMessage('Error subiendo la imatgen.', 'error')
    }
}

// Menu de client personalitzat
const updateHasCustomMenu = async (item, value) => {
  const prev = Boolean(item.has_custom_menu)
  item.has_custom_menu = value
  try {
    await axios.patch(`/api/admin/clients/${item.id}/has-custom-menu`,
      { has_custom_menu: Number(!!value) },
      { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
    )
    showAlertMessage('Preferència guardada.', 'success')
  } catch (e) {
    item.has_custom_menu = prev
    console.error('Error actualitzant has_custom_menu:', e?.response?.data || e)
    showAlertMessage('Error guardant el canvi.', 'error')
  }
}

// Mapa fotos
const updateHasMapaFotos = async (item, value) => {
  const prev = Boolean(item.has_mapa_fotos)
  item.has_mapa_fotos = value

  try {
    await axios.patch(`/api/admin/clients/${item.id}/has-mapa-fotos`,
      { has_mapa_fotos: Number(!!value) },
      { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
    )

    showAlertMessage('Preferència de mapa guardada.', 'success')
  } catch (e) {
    item.has_mapa_fotos = prev
    console.error('Error actualitzant has_mapa_fotos:', e?.response?.data || e)
    showAlertMessage('Error guardant el canvi de mapa.', 'error')
  }
}

// Taula headers
const headers = [
    { title: 'ID', value: 'id', sortable: true },
    { title: 'ID Odoo', value: 'odoo_id', sortable: true },
    { title: 'Cliente', value: 'name', sortable: true },
    { title: 'Nombre comercial', value: 'nom_comercial', sortable: true },
    { title: 'Menú personalizado', value: 'has_custom_menu', sortable: true },
    { title: 'Fotos en el mapa', value: 'has_mapa_fotos', sortable: true },
    { title: 'Logo', value: 'logo', sortable: false },
    { title: 'Imagen', value: 'image', sortable: false },
    { title: 'Acciones', value: 'actions', sortable: false }
]

onMounted(fetchClients)
</script>
